/**
 * Təyinat toqquşma yoxlaması – buraxılış üçün sual səviyyəli toqquşma.
 */

import type { YoklaAssignment, StudentIdPrefixFilter } from '../types';

/**
 * Təyinatın sual siyahısını array kimi qaytarır (questionNumbers və ya questionNumber).
 */
export function getAssignmentQuestionNumbers(a: YoklaAssignment): string[] {
  if (a.questionNumbers && a.questionNumbers.length > 0) {
    return [...a.questionNumbers];
  }
  if (a.questionNumber) {
    return [a.questionNumber];
  }
  return [];
}

/**
 * Returns true if two prefix filters cover overlapping student sets.
 *
 * Rules:
 * - No filter (or disabled) = covers ALL students.
 * - If either side covers all students the sets overlap by definition.
 * - If both sides have explicit prefix lists they overlap only when the
 *   lists share at least one prefix.
 */
export function prefixFiltersOverlap(
  filterA: StudentIdPrefixFilter | undefined,
  filterB: StudentIdPrefixFilter | undefined
): boolean {
  const aActive = filterA && filterA.enabled && filterA.prefixes.length > 0;
  const bActive = filterB && filterB.enabled && filterB.prefixes.length > 0;

  // At least one side covers ALL students → always overlaps
  if (!aActive || !bActive) return true;

  // Both have explicit lists → overlaps only when the lists intersect
  const aSet = new Set(filterA!.prefixes);
  return filterB!.prefixes.some((p) => aSet.has(p));
}

export interface BuraxilisCollisionResult {
  hasCollision: boolean;
  teacherName?: string;
  conflictingQuestions?: string[];
}

/**
 * Buraxılış üçün: mövcud təyinatların heç birinin sualları ilə newQuestionNumbers kəsişməməlidir.
 * Kəsişmə varsa birinci toqquşan təyinatın müəllim adı və kəsişən suallar qaytarılır.
 *
 * Prefix filter logic:
 * - If existing assignment and new assignment have non-overlapping prefix filters
 *   they serve different student sets, so the same question number is NOT a collision.
 */
export function checkBuraxilisQuestionCollision(
  existingAssignments: YoklaAssignment[],
  newQuestionNumbers: string[],
  newPrefixFilter?: StudentIdPrefixFilter
): BuraxilisCollisionResult {
  const newSet = new Set(newQuestionNumbers);
  if (newSet.size === 0) {
    return { hasCollision: false };
  }

  for (const existing of existingAssignments) {
    // If the student sets do not overlap, there is no real collision
    if (!prefixFiltersOverlap(existing.studentIdPrefixFilter, newPrefixFilter)) {
      continue;
    }

    const existingQuestions = getAssignmentQuestionNumbers(existing);
    const conflicting: string[] = existingQuestions.filter((q) => newSet.has(q));
    if (conflicting.length > 0) {
      return {
        hasCollision: true,
        teacherName: existing.hocaName || existing.hocaId,
        conflictingQuestions: conflicting,
      };
    }
  }

  return { hasCollision: false };
}
